// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { ScoreLevel } from './base-metric.entity';

/**
 * Influence type for tooltip context
 */
export type InfluenceType = 'technical' | 'ecosystem';

/**
 * Configuration for influence tooltip display
 */
export interface InfluenceTooltipConfig {
  /** Tooltip text for technical influence */
  technicalInfluence: string;
  /** Tooltip text for ecosystem influence */
  ecosystemInfluence: string;
  /** Combined tooltip explaining both influence types */
  combinedInfluence: string;
  /** Level-specific tooltip definitions */
  levels: Record<ScoreLevel, string>;
}

/**
 * InfluenceTooltip Entity
 *
 * Centralizes all tooltip definitions for influence-related terms
 * used throughout the Key-projects feature.
 *
 * Usage:
 * - For influence type tooltips: InfluenceTooltipEntity.getInfluenceTypeTooltip('technical')
 * - For level tooltips: InfluenceTooltipEntity.getLevelTooltip('Leading')
 * - For combined tooltip: InfluenceTooltipEntity.getCombinedInfluenceTooltip()
 */
export class InfluenceTooltipEntity {
  /**
   * Core definition explaining both influence types
   */
  private static readonly INFLUENCE_DEFINITION =
    'Technical influence examines code activities (commits, PRs) while ecosystem influence examines non-code collaboration activities (documentation, committees, meetings, events).';

  /**
   * Comparison explanation for influence metrics
   */
  private static readonly INFLUENCE_COMPARISON =
    "Comparing our company's share of these activities to the project total indicates greater influence in the project.";

  /**
   * Activity count tooltip definition
   */
  private static readonly ACTIVITIES_DEFINITION =
    "Code contributions directly impact a project's code repositories. Collaborations refer to activities associated with engagement or coordination with others, and don't reflect technical-driven impact.";

  /**
   * Complete configuration for all influence tooltips
   */
  protected static readonly CONFIG: InfluenceTooltipConfig = {
    technicalInfluence: this.getInfluenceTooltipHtml(),
    ecosystemInfluence: this.getInfluenceTooltipHtml(),
    combinedInfluence: this.getInfluenceTooltipHtml(),
    levels: {
      Silent: 'Silent: no participation of this type.',
      Participating:
        'Participating: a small but non-zero share of activities, showing intent to help improve the project.',
      Contributing:
        'Contributing: a small-to-medium share of activities, showing commitment and continued involvement.',
      Leading:
        'Leading: a medium-to-large share of activities, providing critical mass and showing serious dedication to building the project.',
    },
  };

  /**
   * Get the full configuration
   */
  static getConfig(): InfluenceTooltipConfig {
    return this.CONFIG;
  }

  /**
   * Get tooltip text for a specific influence type
   * @param type - 'technical' or 'ecosystem'
   * @returns Tooltip text for the influence type
   */
  static getInfluenceTypeTooltip(type: InfluenceType): string {
    return type === 'technical'
      ? this.CONFIG.technicalInfluence
      : this.CONFIG.ecosystemInfluence;
  }

  /**
   * Get tooltip text for a specific influence level
   * @param level - The influence level (Silent, Participating, Contributing, Leading)
   * @returns Tooltip text for the level
   */
  static getLevelTooltip(level: ScoreLevel): string {
    return this.CONFIG.levels[level] || '';
  }

  /**
   * Get the combined influence tooltip (same for both types)
   * @returns Combined tooltip text
   */
  static getCombinedInfluenceTooltip(): string {
    return this.CONFIG.combinedInfluence;
  }

  /**
   * Get tooltip HTML for table header (formatted as list)
   * Used for table column headers where HTML formatting is supported
   * @returns HTML formatted tooltip content
   */
  static getInfluenceTooltipHtml(): string {
    return `
    <ul>
      <li>${this.INFLUENCE_DEFINITION}</li>
      <li>${this.INFLUENCE_COMPARISON}</li>
    </ul>
  `;
  }

  /**
   * Get tooltip text for activity count leaderboards
   * @returns Tooltip text explaining contributions vs collaborations
   */
  static getActivitiesTooltip(): string {
    return this.ACTIVITIES_DEFINITION;
  }

  /**
   * Check if a string is a valid influence level
   * @param level - String to check
   * @returns True if valid ScoreLevel
   */
  static isValidLevel(level: string): level is ScoreLevel {
    return ['Silent', 'Participating', 'Contributing', 'Leading'].includes(level);
  }

  /**
   * Get all available influence levels
   * @returns Array of all ScoreLevel values
   */
  static getAllLevels(): readonly ScoreLevel[] {
    return ['Silent', 'Participating', 'Contributing', 'Leading'] as const;
  }
}
